import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { laserEngravingAccessForUser } from '../../../utils/laserEngravingAccess';
import { DEFINITION_VERSION } from '../northstar-browser/identity';
import { DeliveryNotice, ServerError, fieldStyle, mutedStyle, panelStyle, toolPath, useToolApi, useVisibleRefresh } from './toolApi';

const browserTools = [
  {
    id: 'northstar', name: 'Northstar', category: 'Calibration', status: 'Test', version: `Browser ${DEFINITION_VERSION}`,
    description: 'Display calibration and ECDIS verification. Scan a service tag to find its configuration and retain the Test results with the monitor.',
    scope: 'CA-410 meter · Chroma 2233-B · Monitor control',
    note: 'Hardware runs require a qualified bench and a ready configuration package. Simulation is available now.',
    path: '/quality/northstar/browser', action: 'Open Northstar',
    demo: '/quality/northstar/browser?serviceTag=TEST-NORTHSTAR-DEMO', demoAction: 'Try Northstar simulation',
  },
  {
    id: 'uid-artwork', name: 'UID artwork', category: 'Labelling', status: 'Browser tool', version: 'Wave XL MON · Test MON',
    description: 'Create service tags, review the UID layout and prepare artwork for laser engraving. Keep issued identifiers and artwork records together in QMS.',
    scope: 'Service tag · QR code · UID artwork',
    note: 'Prepare SVG or printable artwork here, then use the laser station to mark the product.',
    path: '/quality/laser-engraving', action: 'Open UID artwork',
    demo: '/quality/laser-engraving?mode=test', demoAction: 'Try Test artwork',
  },
];
const cardGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '1rem' };
const badge = { display: 'inline-block', padding: '.25rem .65rem', borderRadius: 20, fontSize: '.8rem', fontWeight: 700,
  border: '1px solid var(--ni-border-soft)', color: 'var(--ni-green)' };

function BrowserToolCard({ tool }) {
  return <article style={{ ...panelStyle, background: 'var(--ni-panel)', padding: '1.5rem', marginBottom: 0,
    display: 'flex', flexDirection: 'column', minWidth: 0 }} aria-label={tool.name}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '.5rem' }}>
      <span style={{ ...mutedStyle, textTransform: 'uppercase', letterSpacing: '.08em' }}>{tool.category}</span>
      <span style={badge}>{tool.status}</span>
    </div>
    <h3 style={{ fontSize: '2rem', margin: '.8rem 0 .6rem' }}>{tool.name}</h3>
    <p style={{ lineHeight: 1.6, margin: '0 0 1rem' }}>{tool.description}</p>
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', margin: '.4rem 0 1rem' }}>
      <Link className="ni-button ni-button--primary" style={{ textDecoration: 'none' }} to={tool.path}>{tool.action} →</Link>
      <Link to={tool.demo}>{tool.demoAction}</Link>
    </div>
    <p style={{ ...mutedStyle, lineHeight: 1.5 }}>{tool.scope}</p>
    <p style={{ ...mutedStyle, lineHeight: 1.5, flexGrow: 1 }}>{tool.note}</p>
    <p style={{ ...mutedStyle, fontSize: '.8rem', margin: '1.1rem 0 0' }}>{tool.version}</p>
  </article>;
}

export default function TestToolList() {
  const api = useToolApi();
  const { userId, sessionId } = useAuth();
  const { user } = useUser();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [before, setBefore] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [engineeringOpen, setEngineeringOpen] = useState(false);
  const scope = JSON.stringify([userId, sessionId, search, before, refresh]);
  const data = record?.scope === scope ? record.body : null;
  const generation = useRef(0);
  const pending = useRef(false);
  useEffect(() => { setEngineeringOpen(false); }, [userId, sessionId]);
  useVisibleRefresh(() => { if (!pending.current) setRefresh(value => value + 1); });
  useEffect(() => {
    const current = ++generation.current, controller = new AbortController();
    setRecord(null); setError(null); setLoading(true); pending.current = true;
    const params = new URLSearchParams({ ...(search ? { search } : {}), ...(before ? { before } : {}) });
    const queryString = params.toString();
    api(`/quality/test-tools${queryString ? `?${queryString}` : ''}`, { signal: controller.signal })
      .then(body => {
        if (!Array.isArray(body.tools)) throw new Error('The server did not return a tool catalogue.');
        if (current === generation.current) setRecord({ scope, body });
      })
      .catch(reason => { if (current === generation.current && reason.name !== 'AbortError') setError(reason); })
      .finally(() => { if (current === generation.current) { setLoading(false); pending.current = false; } });
    return () => { generation.current += 1; controller.abort(); };
  }, [api, search, before, scope]);
  const allowed = laserEngravingAccessForUser(user).allowed;
  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  const applications = allowed ? browserTools.filter(tool => {
    const content = `${tool.name} ${tool.category} ${tool.description} ${tool.scope}`.toLowerCase();
    return terms.every(term => content.includes(term));
  }) : [];

  return <div>
    <div style={{ marginBottom: '1.5rem' }}>
      <h1 style={{ marginBottom: '.6rem' }}>Tools and software</h1>
      <p style={{ ...mutedStyle, fontSize: '1rem', lineHeight: 1.6, maxWidth: 750 }}>Open your tools directly in QMS. Choose a tool below, then follow its setup and service tag workflow.</p>
    </div>
    <form onSubmit={event => { event.preventDefault(); setBefore(''); setSearch(query.trim()); setRefresh(value => value + 1); }}
      style={{ display: 'flex', flexWrap: 'wrap', gap: '.75rem', marginBottom: '1.5rem' }}>
      <input aria-label="Find a tool" placeholder="Find a tool by name or purpose" maxLength={100} style={{ ...fieldStyle, flex: '1 1 240px' }} value={query} onChange={event => setQuery(event.target.value)} />
      <button className="ni-button" type="submit">Search</button>
      {search && <button className="ni-button" type="button" onClick={() => { setQuery(''); setSearch(''); setBefore(''); }}>Clear search</button>}
    </form>
    {loading && <p role="status">Loading tools…</p>}
    <ServerError error={error} />
    {data && <>
      <section aria-label="Browser tools" style={{ marginBottom: '1.5rem' }}>
        <h2>Browser tools</h2>
        <p style={{ ...mutedStyle, lineHeight: 1.5 }}>These applications run in QMS and receive updates with the system.</p>
        {!!applications.length ? <div style={cardGrid}>{applications.map(tool => <BrowserToolCard key={tool.id} tool={tool} />)}</div>
          : <p>{search ? 'No browser tools match this search.' : 'No browser tools are available to this account.'}</p>}
      </section>
      {!search && <section aria-label="Station workflows" style={{ ...panelStyle, padding: '1.25rem', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div><h2 style={{ margin: '0 0 .5rem' }}>Working at a production station?</h2>
          <p style={{ ...mutedStyle, margin: 0, lineHeight: 1.5 }}>Open the station workflow for its product instructions, tests and recorded evidence.</p></div>
        <Link className="ni-button" style={{ textDecoration: 'none' }} to="/operate">Open station workflow →</Link>
      </section>}
      <details open={engineeringOpen} onToggle={event => setEngineeringOpen(event.currentTarget.open)} style={{ ...panelStyle, marginTop: '1.5rem' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Engineering and version history</summary>
        <p style={{ ...mutedStyle, lineHeight: 1.5 }}>Retained software releases and desktop packages are available for engineering and historical records. Their versions are separate from the browser applications above.</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '1rem 0' }}>
          <Link to="/quality/test-runtime">Browser test lab · synthetic examples</Link>
          {data.permissions?.canManage === true && <>
            <Link to="/quality/northstar/packages">Northstar configuration preparation</Link>
            <Link to="/quality/test-tools/new">Register tool</Link>
          </>}
        </div>
        <h2>Registered release records</h2>
        {!data.tools.length && <p>{search ? 'No registered release records match this search.' : 'No registered release records are available to this account.'}</p>}
        {!!data.tools.length && <div className="ni-table-wrap"><table className="ni-table">
          <thead><tr><th>Tool</th><th>Type</th><th>Available archived version</th><th>Status</th><th>Records</th></tr></thead>
          <tbody>{data.tools.map(tool => <tr key={tool.documentNumber}>
            <td><Link to={toolPath(tool.documentNumber)}>{tool.name}</Link><div style={mutedStyle}>{tool.documentNumber}</div></td>
            <td>{tool.toolType}</td>
            <td>{tool.latestRelease ? <><Link to={toolPath(tool.documentNumber)}>{tool.latestRelease.tag}</Link>{tool.latestRelease.prerelease ? ' · Prerelease' : ''}</> : 'No stored version available'}
              <DeliveryNotice deliveryStatus={tool.deliveryStatus} currentRelease={tool.latestRelease} /></td>
            <td>{tool.status}</td><td><Link to={toolPath(tool.documentNumber)}>Versions and engineering</Link></td>
          </tr>)}</tbody>
        </table></div>}
        {data.pagination?.nextBefore && <button className="ni-button" disabled={loading} onClick={() => setBefore(data.pagination.nextBefore)}>Older catalogue entries</button>}
        {before && <button className="ni-button" disabled={loading} onClick={() => setBefore('')}>Newest catalogue entries</button>}
      </details>
    </>}
  </div>;
}
